import React from 'react';
import { ExternalLink, ShieldAlert } from 'lucide-react';
import { theme } from '../shared/theme';
import { useLanguage } from '../../components/LanguageContext';
import { useViewportFill } from '../shared/useViewportFill';
import '../shared/portals.css';

// ============================================================
// ConsoleFrame — one of the two internal consoles, inside the app.
//
// Asset Desk (/admin) and Property Management (/api/pm/admin) are not React.
// They are two large server-rendered pages, 315KB and 180KB, that the team
// has worked in for months. Rebuilding either to get it into a tab would be
// months of work to arrive back where we started, so the tab shows the real
// console.
//
// ---- WHY IT IS NOT EMBEDDED YET -----------------------------------------
//
// Framing them is blocked today, by us, in three places:
//
//   1. the backend sends `X-Frame-Options: DENY` on every response, and the
//      PM console adds `frame-ancestors 'none'` on top (Backend/pmRoutes.js,
//      Backend/server.js);
//   2. the hosting CSP has no `frame-src` entry for the backend origin
//      (firebase.json);
//   3. the admin session cookie is `SameSite=strict`, so it is not sent
//      inside a cross-origin frame at all — which is what gates the Asset
//      Desk page server-side.
//
// The clean fix would have been a Firebase Hosting rewrite putting the
// consoles on the portal's own origin, making the frame same-origin and the
// cookie first-party. That is NOT available here: Hosting lives in project
// investor-database-c0c49 and Cloud Run in project investmenter-backend, and
// a rewrite can only target Cloud Run in its own project. Verified — the
// rewrite deploy fails with exactly that.
//
// So embedding means relaxing (1) and (2) to an allow-list of our own front
// ends, and solving (3) for Asset Desk some other way, because third-party
// cookies are blocked by default in current browsers regardless of what we
// send. That is a security decision, so it is the user's to take and this
// switch is where it lands.
//
// FLIP `EMBED` TO true once those headers allow it. Nothing else changes.
// ============================================================

const EMBED = false;

const DEV_ORIGIN = 'http://localhost:4002';
const LIVE_BACKEND = 'https://investmenter-backend-1081814658115.europe-west1.run.app';
const PROD = process.env.NODE_ENV === 'production';
const ORIGIN = PROD ? LIVE_BACKEND : DEV_ORIGIN;

export const CONSOLE_URLS = {
  assetDesk: `${ORIGIN}/admin`,
  propertyManagement: PROD ? `${ORIGIN}/api/pm/admin` : `${DEV_ORIGIN}/pm-admin`,
};

interface Props {
  src: string;
  title: string;
  /** One line of context under the toolbar. */
  note?: string;
}

const ConsoleFrame: React.FC<Props> = ({ src, title, note }) => {
  const { t } = useLanguage();
  const ref = useViewportFill<HTMLDivElement>();

  if (!EMBED) {
    return (
      <div className="ea-frame" ref={ref} style={{ background: theme.bg, justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', padding: '28px 22px', maxWidth: 420, margin: '0 auto' }}>
          <ShieldAlert size={30} color={theme.gold} />
          <div style={{ fontSize: 16.5, fontWeight: 800, color: theme.ink, marginTop: 10 }}>{title}</div>
          <div style={{ fontSize: 13.5, lineHeight: 1.5, color: theme.slate, marginTop: 6 }}>
            {t('eaConsoleNotEmbedded')}
          </div>
          <a
            href={src}
            target="_blank"
            rel="noopener noreferrer"
            className="pt-tap"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 7, marginTop: 16,
              background: theme.navy, color: '#fff', borderRadius: 12,
              padding: '11px 18px', fontSize: 14, fontWeight: 700, textDecoration: 'none',
            }}
          >
            <ExternalLink size={16} /> {t('eaOpenConsole')}
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="ea-frame" ref={ref}>
      <div className="ea-frame-bar">
        <span className="ea-frame-note">{note || title}</span>
        {/* Always present, never only on failure: a frame that fails to load
            cannot reliably tell us it did, and a console the team cannot
            escape to a full window is worse than one they can. */}
        <a className="ea-frame-link" href={src} target="_blank" rel="noopener noreferrer">
          <ExternalLink size={13} /> {t('eaOpenFullScreen')}
        </a>
      </div>
      <div className="ea-frame-narrow">{t('eaConsoleNarrow')}</div>
      <iframe src={src} title={title} />
    </div>
  );
};

export default ConsoleFrame;
